/**
 * Subscription API client
 * Handles all billing and subscription-related API calls
 */

import apiClient from './client';
import type {
  Plan,
  UserSubscription,
  ValidateCouponRequest,
  ValidateCouponResponse,
  CreateSubscriptionRequest,
  CheckUsageLimitResponse,
  TrialStatus,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
} from '@/types/subscription';

// Plans endpoints
export const subscriptionApi = {
  /**
   * Get all available plans
   */
  async getPlans(): Promise<Plan[]> {
    const response = await apiClient.get('/subscriptions/plans/');
    return response.data.results || response.data;
  },

  /**
   * Get specific plan by ID
   */
  async getPlan(planId: string): Promise<Plan> {
    const response = await apiClient.get(`/subscriptions/plans/${planId}/`);
    return response.data;
  },

  /**
   * Validate a coupon code
   */
  async validateCoupon(request: ValidateCouponRequest): Promise<ValidateCouponResponse> {
    const response = await apiClient.post('/subscriptions/coupons/validate/', request);
    return response.data;
  },

  /**
   * Get user's current subscription
   */
  async getCurrentSubscription(): Promise<UserSubscription> {
    const response = await apiClient.get('/subscriptions/me/');
    return response.data;
  },

  /**
   * Create a new subscription
   */
  async createSubscription(request: CreateSubscriptionRequest): Promise<UserSubscription> {
    const response = await apiClient.post('/subscriptions/create/', request);
    return response.data;
  },

  /**
   * Upgrade to a higher plan
   */
  async upgradePlan(planCode: string, couponCode?: string): Promise<UserSubscription> {
    const response = await apiClient.post('/subscriptions/upgrade/', {
      plan_code: planCode,
      coupon_code: couponCode,
    });
    return response.data;
  },

  /**
   * Downgrade to a lower plan
   */
  async downgradePlan(planCode: string, couponCode?: string): Promise<UserSubscription> {
    const response = await apiClient.post('/subscriptions/downgrade/', {
      plan_code: planCode,
      coupon_code: couponCode,
    });
    return response.data;
  },

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<UserSubscription> {
    const response = await apiClient.post('/subscriptions/cancel/');
    return response.data;
  },

  /**
   * Check usage limits for current subscription
   */
  async checkUsageLimit(): Promise<CheckUsageLimitResponse> {
    const response = await apiClient.get('/subscriptions/usage/');
    return response.data;
  },

  async getTrialStatus(): Promise<TrialStatus> {
    const response = await apiClient.get('/subscriptions/trial/');
    return response.data;
  },

  async startTrial(): Promise<TrialStatus> {
    const response = await apiClient.post('/subscriptions/trial/start/');
    return response.data;
  },

  async initiatePayment(request: InitiatePaymentRequest): Promise<InitiatePaymentResponse> {
    const response = await apiClient.post('/subscriptions/pay/', request);
    return response.data;
  },
};
